import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import posts from '../../data/cloned/marketing.json';

const allPosts = (posts as any).posts as Record<string, any>;
const allPostSlugs = Object.keys(allPosts);

/** Blog index — uses the actual cloned blog.html. */
export function BlogIndex() {
  return <ClonedBlogPage source="blog.html" title="Blog — Funder Pro" />;
}

/** Blog post — renders the actual cloned blog/<slug>.html. */
export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  if (!slug || !allPostSlugs.includes(slug)) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="text-3xl font-bold">Post not found</h1>
        <Link to="/blog" className="mt-4 inline-block text-[#7e9aff]">Back to blog</Link>
      </div>
    );
  }
  return <ClonedBlogPage source={`blog/${slug}.html`} title={allPosts[slug].h1 || slug} />;
}

function ClonedBlogPage({ source, title }: { source: string; title: string }) {
  const [error, setError] = useState<string | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`/cloned/${source}`, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`Failed to load ${source}: ${res.status}`);
        const html = await res.text();
        if (cancelled || !container) return;
        const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
        let body = bodyMatch ? bodyMatch[1] : html;
        body = body.replace(/<header\b[^>]*>[\s\S]*?<\/header>/gi, '');
        body = body.replace(/<footer\b[^>]*>[\s\S]*?<\/footer>/gi, '');
        body = body.replace(/<button[^>]*aria-label="Go to top"[^>]*>[\s\S]*?<\/button>/gi, '');
        body = body.replace(/<noscript>[\s\S]*?<\/noscript>/gi, '');
        body = body.replace(/<style[^>]*data-emotion[^>]*>[\s\S]*?<\/style>/gi, '');
        // Make all content visible (strip scroll-animation initial states)
        body = body.replace(/style="[^"]*opacity:\s*0[^"]*"/gi, 'style="opacity:1"');
        body = body.replace(/style="[^"]*opacity:\s*0;[^"]*transform:[^"]*"/gi, (m) => m.replace(/opacity:\s*0/, 'opacity:1').replace(/transform:[^;"]*/g, 'transform:none'));
        body = body.replace(/opacity:0/g, 'opacity:1');
        body = body.replace(/transform:translateY\([^)]+\)/g, 'transform:none');
        body = body.replace(/transform:translateX\([^)]+\)/g, 'transform:none');

        // ---- Step 1: map static marketing pages to React Router paths ----
        const MKT_PAGES = [
          'how-it-works', 'about-us', 'blog', 'contact-us',
          'terms-conditions', 'privacy-notice', 'cookie-notice',
          'risk-disclosure', 'risk-disclaimer', 'gold-offer',
          'link-tree', 'sitemap',
        ];
        const mkPageRe = (page: string, suffix: string) =>
          new RegExp(`href="\\\\?/?${page}${suffix}(#[^"']*)?\\\\?"`, 'g');
        for (const p of MKT_PAGES) {
          body = body.replace(mkPageRe(p, '\\.html'), `href="/${p}$1"`);
          body = body.replace(mkPageRe(p, '/index\\.html'), `href="/${p}$1"`);
          body = body.replace(new RegExp(`href="\\\\?/?${p}/\\\\?"`, 'g'), `href="/${p}"`);
        }
        // Special: any bare `index.html` — map to /
        body = body.replace(/href="\/?index\.html(#[^"']*)?"/g, 'href="/$1"');
        body = body.replace(/href="index\.html(#[^"']*)?"/g, 'href="/$1"');

        // ---- Step 2: fix mangled paths ----
        body = body.replace(/href="\/contact-usen\/articles\/[^"]*"/g, 'href="/contact-us"');

        // ---- Step 2.5: rewrite absolute funderprofutures.com links so we
        // never navigate away from this deployment. ----
        body = body.replace(/href="https?:\/\/checkout\.funderprofutures\.com\/checkout\/purchase\/fut-zero-(\d+)k"[^>]*?/g,
          (_m, sz) => `href="/portal/challenge/tier?size=${sz}k" data-cloned-link="true"`);
        body = body.replace(/href="https?:\/\/checkout\.funderprofutures\.com\/products[^"]*"/g,
          'href="/portal/platforms" data-cloned-link="true"');
        body = body.replace(/href="https?:\/\/dashboard\.funderprofutures\.com\/Identity[^"]*"/g,
          'href="/login" data-cloned-link="true"');
        body = body.replace(/href="https?:\/\/dashboard\.funderprofutures\.com\/EconomicCalendar[^"]*"/g,
          'href="/portal/economic-calendar" data-cloned-link="true"');
        body = body.replace(/href="https?:\/\/support\.funderprofutures\.com\/?"/g,
          'href="/portal/help" data-cloned-link="true"');
        body = body.replace(/href="https?:\/\/dashboard\.funderprofutures\.com\/?"/g,
          'href="/portal/dashboard" data-cloned-link="true"');
        body = body.replace(/href="https?:\/\/dashboard\.funderprofutures\.com[^"]*"/g,
          'href="/portal/dashboard" data-cloned-link="true"');
        body = body.replace(/href="https?:\/\/funderprofutures\.com\/cdn-cgi\/l\/email-protection[^"]*"/g,
          'href="/contact-us" data-cloned-link="true"');
        body = body.replace(/href="https?:\/\/(?:www\.)?funderprofutures\.com((?:\/[^"']*)?)"/g,
          (_m, rest) => `href="${rest || '/'}" data-cloned-link="true"`);

        // ---- Step 3: rewrite asset paths to /cloned/ ----
        body = body.replace(/href="(\/?(?:wp-content|icons|fonts|_next|original\.css|original-typography\.css|media-logo\.png|sitemap\.xml|robots\.txt|gold-offer\/index\.html)[^"#?]*)"/g,
          (_m, p) => `href="/cloned/${p.replace(/^\//, '')}"`);
        body = body.replace(/src="(\/?(?:wp-content|icons|fonts|_next|original\.css|original-typography\.css|media-logo\.png|sitemap\.xml|robots\.txt|gold-offer\/index\.html)[^"#?]*)"/g,
          (_m, p) => `src="/cloned/${p.replace(/^\//, '')}"`);
        body = body.replace(/url\(\s*['"]?(\/?(?:wp-content|icons|fonts|_next|original\.css|original-typography\.css|media-logo\.png|sitemap\.xml|robots\.txt|gold-offer\/index\.html)[^'")]+)['"]?\s*\)/g,
          (_m, p) => `url("/cloned/${p.replace(/^\//, '')}")`);
        body = body.replace(/content="(\/?(?:wp-content|icons|fonts|_next|original\.css|original-typography\.css|media-logo\.png|sitemap\.xml|robots\.txt|gold-offer\/index\.html)[^"]*)"/g,
          (_m, p) => `content="/cloned/${p.replace(/^\//, '')}"`);

        // ---- Step 4: blog post links to React Router ----
        // Run AFTER the asset rewriter so the previously-mangled `/cloned/blog/<slug>`
        // paths are converted to clean React Router paths. Also catches the relative
        // `blog/<slug>.html` form (not rewritten by the asset rewriter).
        body = body.replace(/href="\/?blog\/([a-z0-9-]+)(?:\.html|\/index\.html|\/)?([?][^"']*)?(#[^"']*)?"/g,
          (_m, slug, qs, hash) => `href="/blog/${slug}${qs || ''}${hash || ''}" data-cloned-link="true"`);
        body = body.replace(/href="\/cloned\/blog\/([a-z0-9-]+)(?:\.html|\/index\.html|\/)?([?][^"']*)?(#[^"']*)?"/g,
          (_m, slug, qs, hash) => `href="/blog/${slug}${qs || ''}${hash || ''}" data-cloned-link="true"`);
        body = body.replace(/href="\/?blog\.html([?][^"']*)?(#[^"']*)?"/g,
          (_m, qs, hash) => `href="/blog${qs || ''}${hash || ''}" data-cloned-link="true"`);
        body = body.replace(/href="\/?blog\/index\.html([?][^"']*)?(#[^"']*)?"/g,
          (_m, qs, hash) => `href="/blog${qs || ''}${hash || ''}" data-cloned-link="true"`);

        // ---- Step 5: clean up any remaining bad paths ----
        body = body.replace(/<link[^>]*href="\/?_next\/[^"]*"[^>]*>/g, '');
        body = body.replace(/<script[^>]*src="\/?_next\/[^"]*"[^>]*><\/script>/g, '');
        body = body.replace(/<link[^>]*href="\/?_next\/[^"]*"[^>]*\/>/g, '');
        body = body.replace(/<script>self\.__next_f[^<]*<\/script>/g, '');

        container.innerHTML = body;
        if (title) document.title = title;
        container.querySelectorAll('script').forEach(oldScript => {
          const newScript = document.createElement('script');
          for (const attr of oldScript.attributes) newScript.setAttribute(attr.name, attr.value);
          newScript.textContent = oldScript.textContent;
          oldScript.parentNode?.replaceChild(newScript, oldScript);
        });
        // Wire up FAQ accordions + carousels
        const wire = (window as any).__wireClonedInteractions;
        if (typeof wire === 'function') setTimeout(wire, 50);
        else setTimeout(() => { const w = (window as any).__wireClonedInteractions; if (typeof w === 'function') w(); }, 500);
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    }
    if (container) load();
    return () => { cancelled = true; };
  }, [source, title, container]);

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="text-2xl font-bold text-red-500">Could not load page</h1>
        <p className="mt-4 text-[#9cabd6]">{error}</p>
        <Link to="/blog" className="mt-6 inline-block text-[#7e9aff] hover:text-white">Back to blog</Link>
      </div>
    );
  }

  return (
    <div className="cloned-page">
      <div ref={setContainer} />
    </div>
  );
}
